/**
 * ActivityRegistrationBlock
 * Registration section on Activity Detail page.
 * Karmana: always visible. External: shown when interested.
 * Props:
 *   showMessage  — true when user clicked "I'm Interested" → show prompt banner
 */
import { ExternalLink, CheckCircle } from 'lucide-react';
import KarmanaRegistrationForm from './KarmanaRegistrationForm';
import GoogleFormEmbed from './GoogleFormEmbed';
import { track } from '../utils/analytics';
import { triggerGoogleFormsSync } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ActivityRegistrationBlock({
  activityId,
  registrationType,
  registrationLink,
  registrationClosed,
  registrationFull,
  registrationFields,
  registrationRequirement,
  registrationEndDate,    // YYYY-MM-DD; optional registration cutoff
  alreadyRegistered,
  user,
  onSuccess,
  onOTPRequired,
  blockRef,
  showMessage = false,
  sourceProps = {},
  onFieldsChange,
  activityDate,       // activity.date (YYYY-MM-DD)
  activityDateEnd,    // activity.date_end (YYYY-MM-DD), optional
  isOngoing,          // activity.is_ongoing
  embedUrl = null,    // Google Forms embed URL (set when forms_sync is active)
}) {
  const isKarmana = registrationType === 'karmana';
  const { user: authUser, openAuthModal } = useAuth();

  // Form is inactive after registration_end_date (if set), otherwise after activity's last date
  const isFormExpired = (() => {
    if (registrationClosed) return true;
    // registration_end_date takes precedence over activity date for cutoff
    if (registrationEndDate) {
      return new Date(`${registrationEndDate}T23:59:59`) < new Date();
    }
    if (isOngoing) return false;
    const lastDate = activityDateEnd || activityDate;
    if (!lastDate) return false;
    return new Date(`${lastDate}T23:59:59`) < new Date();
  })();

  // Format date as "15 Oct 2026"
  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ── Handle Google Form submission (embedded) ───────────────────────────────
  const handleFormSubmitted = async () => {
    try { await triggerGoogleFormsSync(activityId); } catch { /* sync will catch up in 15 min */ }
    if (authUser) {
      toast.success('Registration submitted! Your spot will be confirmed within a minute.', { duration: 5000 });
      // Refresh engagement data after brief delay so sync can process
      setTimeout(() => onSuccess?.(), 5000);
    } else {
      openAuthModal('form_submitted');
    }
  };

  // ── External link block ─────────────────────────────────────────────────────
  if (!isKarmana) {
    // Show embedded form when sync is active (replaces external link)
    if (embedUrl && !registrationClosed) {
      return (
        <div ref={blockRef} className="mt-6 mb-2" data-testid="reg-block-embedded-form">
          {alreadyRegistered ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center border border-green-200 bg-green-50 rounded-lg">
              <CheckCircle size={36} className="text-green-500" />
              <p className="text-base font-bold text-gray-800">Registration Done!</p>
              <p className="text-sm text-gray-500">You&apos;re all set for this activity.</p>
            </div>
          ) : (
            <GoogleFormEmbed
              embedUrl={embedUrl}
              onSubmitted={handleFormSubmitted}
            />
          )}
        </div>
      );
    }

    if (!registrationLink || registrationClosed) return null;
    return (
      <div
        ref={blockRef}
        className="mt-6 mb-2 border border-amber-200 bg-amber-50 p-5"
        data-testid="reg-block-external"
      >
        <p className="text-sm font-bold text-gray-800 mb-1">One more step</p>
        <p className="text-sm text-gray-600 mb-4">
          Complete the organiser&apos;s registration to secure your spot.
        </p>
        <a
          href={registrationLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reg-block-external-link"
          onClick={() => track('ext_reg_link_click', { activity_id: activityId, source_page: 'activity_detail' })}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-colors"
        >
          Register <ExternalLink size={14} />
        </a>
        {registrationEndDate && (
          <p className="text-xs text-gray-500 mt-3">
            Registration Ends on <span className="font-semibold">{fmtDate(registrationEndDate)}</span>
          </p>
        )}
        <p className="text-xs text-gray-400 mt-2 italic">
          Showing interest on Karmana does not automatically register you.
        </p>
      </div>
    );
  }

  // ── Karmana form block ──────────────────────────────────────────────────────
  const reqLabel =
    registrationRequirement === 'mandatory'
      ? 'Mandatory'
      : registrationRequirement === 'optional'
      ? 'Optional'
      : '';

  return (
    <div
      ref={blockRef}
      className="mt-4 mb-4 overflow-hidden"
      style={{ border: '1px solid #e5e7eb' }}
      data-testid="reg-block-karmana"
    >
      {/* Thin green accent stripe at top */}
      <div className="h-1 bg-gradient-to-r from-green-600 to-emerald-500" />

      {/* Prompt banner — shown when user clicks "I'm Interested" */}
      {showMessage && !alreadyRegistered && (
        <div
          className="flex items-start gap-2 px-5 py-3 bg-green-50 border-b border-green-100"
          data-testid="reg-block-message"
        >
          <span className="text-green-600 mt-0.5 flex-shrink-0">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
            </svg>
          </span>
          <p className="text-sm font-semibold text-green-800 leading-snug">
            Please complete the registration form below to confirm your spot.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="px-5 pt-4 pb-3 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-base font-bold text-gray-900">Register to Participate</p>
          {reqLabel && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                reqLabel === 'Mandatory'
                  ? 'text-green-700 bg-green-50 border-green-200'
                  : 'text-gray-500 bg-gray-50 border-gray-200'
              }`}
            >
              {reqLabel}
            </span>
          )}
        </div>
      </div>

      {/* Form / success state */}
      <div className="px-5 py-5 bg-white">
        {alreadyRegistered ? (
          <div
            className="flex flex-col items-center gap-3 py-4 text-center"
            data-testid="reg-block-done"
          >
            <CheckCircle size={36} className="text-green-500" />
            <p className="text-base font-bold text-gray-800">Registration Done!</p>
            <p className="text-sm text-gray-500">You&apos;re all set for this activity.</p>
          </div>
        ) : (
          <KarmanaRegistrationForm
            activityId={activityId}
            fields={registrationFields || []}
            user={user}
            registrationRequirement={registrationRequirement}
            onSuccess={onSuccess}
            onOTPRequired={onOTPRequired}
            sourceProps={sourceProps}
            disabled={isFormExpired}
            registrationFull={!!registrationFull}
            onFieldsChange={onFieldsChange}
          />
        )}
      </div>
    </div>
  );
}
